import React, { useState } from 'react';
import classnames from 'classnames';

import { makeStyles, useTheme } from '@material-ui/core/styles';

import Accordion from '@material-ui/core/Accordion';
import AccordionSummary from '@material-ui/core/AccordionSummary';
import AccordionDetails from '@material-ui/core/AccordionDetails';

import Icon from '@hydra/common/components/Icon';
import CriteriumAdvice from '@hydra/common/criterium/CriteriumAdvice';
import CriteriumContent from '@hydra/common/criterium/CriteriumContent';
import { useReviewContext } from '@hydra/common/context/review';
import { usePdfTemplateContext } from '@hydra/common/context/pdfTemplate';
import { sendEvent } from '@hydra/utils/gtm';
import { getContrastedColor } from '@hydra/utils/colors';
import { useFeatureFlags } from '@hydra/common/components/FeatureFlags/featureFlagContext';
import { CriteriumInterface } from '@hydra/types/review';

const CRITS_BREAK_INSIDE_AUTO = [
  'shared_count',
  'google_preview',
  'title',
  'backlinks_score',
  'backlinks_counter',
  'backlinks_ref_domains',
  'alexa',
  'trafic_ranking',
  'facebook_brand_page',
  'twitter_account'
];

const useStyles = makeStyles(theme => ({
  pdfCriteriumBreakAvoid: {
    breakInside: 'avoid'
  },
  pdfCriteriumBreakAuto: {
    breakInside: 'auto'
  },
  summary: {
    userSelect: 'auto',
    alignItems: 'start',
    padding: `0 ${theme.spacing(2)}px`,
    paddingRight: 0,
    [theme.breakpoints.down('xs')]: {
      paddingRight: `${theme.spacing(2)}px`
    }
  },
  summaryNewLook: {
    userSelect: 'auto',
    alignItems: 'start',
    padding: `0 ${theme.spacing(2)}px`,
    paddingRight: `${theme.spacing(2)}px`
  },
  root: {
    '&:before': {
      backgroundColor: theme.palette.secondary.lighter
    },
    boxShadow: 'none',
    borderBottom: `1px solid ${theme.palette.secondary.lighter}`
  },
  expandIcon: {
    marginTop: theme.spacing(1),
    [theme.breakpoints.down('xs')]: {
      position: 'absolute',
      right: 0,
      margin: 0,
      marginTop: theme.spacing(0.5)
    }
  },
  expandIconNewLook: {
    position: 'absolute',
    right: 0,
    margin: 0,
    marginTop: theme.spacing(0.5)
  },
  details: {
    [theme.breakpoints.down('xs')]: {
      padding: `${theme.spacing(1)}px ${theme.spacing(2)}px ${theme.spacing(2)}px`
    }
  },
  content: {
    margin: 0,
    padding: theme.spacing(3, 0),
    '&.Mui-expanded': {
      margin: 0
    }
  },
  ctaCursor: { cursor: 'default !important' }
}));

interface CriteriumProps {
  criterium: CriteriumInterface
  children: React.ReactNode
  variables?: Record<string, string>
  clickable?: boolean
  align?: string
}

export const Criterium = ({ children, criterium, variables, clickable = true, align = 'left' }: CriteriumProps) => {
  const classes = useStyles();
  const theme = useTheme();
  const featureFlags = useFeatureFlags();
  const newLookReview = featureFlags && featureFlags.newLookReview;

  const { isPdf } = useReviewContext();
  const { type, score, status } = criterium;
  const { templateColors } = usePdfTemplateContext();
  const iconsFillColorInitialState = isPdf
    ? getContrastedColor(templateColors.primary)
    : theme.palette.secondary.medium;

  const [iconsFillColor, setIconsFillColor] = useState(iconsFillColorInitialState);
  const handleChange = (event: React.ChangeEvent<{}>, expanded: boolean) => {
    if (!clickable) {
      return;
    }
    sendEvent(`review-criteria-tip-chevron-${expanded ? 'open' : 'close'}`, {
      crit: type,
      status
    });
  };

  const onMouseEnter = () => setIconsFillColor(theme.palette.branding.main);
  const onMouseLeave = () => setIconsFillColor(theme.palette.secondary.medium);

  if (isPdf) {
    return (
      <div
        id={`criterium-${type}`}
        className={CRITS_BREAK_INSIDE_AUTO.includes(type) ? classes.pdfCriteriumBreakAuto : classes.pdfCriteriumBreakAvoid}
      >
        <CriteriumContent criterium={criterium} iconsFillColor={iconsFillColor}>
          {children}
        </CriteriumContent>
        <CriteriumAdvice score={score} type={type} variables={variables} />
      </div>
    );
  }

  const summaryClass = newLookReview ? classes.summaryNewLook : classes.summary;
  const expandIconClass = newLookReview ? classes.expandIconNewLook : classes.expandIcon;
  return (
    <Accordion
      id={`criterium-${type}`}
      onChange={handleChange}
      classes={{
        root: classes.root
      }}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
      // See: https://material-ui.com/components/accordion/#performance
      TransitionProps={{ unmountOnExit: true }}
    >
      <AccordionSummary
        className={classnames(`nr-review-${type}-crit`, !clickable && classes.ctaCursor)}
        expandIcon={clickable && <Icon type='chevron-down' />}
        classes={{
          root: summaryClass,
          content: classes.content
        }}
        IconButtonProps={{
          edge: false,
          classes: {
            root: expandIconClass,
            label: `nr-review-${type}-chevron`
          }
        }}
      >
        <CriteriumContent criterium={criterium} align={align} iconsFillColor={iconsFillColor}>
          {children}
        </CriteriumContent>
      </AccordionSummary>
      {clickable
        ? (
          <AccordionDetails className={classnames(classes.details, `nr-review-${type}-tip`)}>
            <CriteriumAdvice score={score} type={type} variables={variables} />
          </AccordionDetails>
          )
        : null}
    </Accordion>
  );
};

export default Criterium;
